import { useEffect, useRef, useState } from 'react';

// ─────────────────────────────────────────────────────────────────────────────
// On-screen controls for phones and tablets (anything with a coarse pointer):
// a joystick under the left thumb to swim, and an action button under the
// right thumb (swing the net or sword, or mine/build in front of you).
// ─────────────────────────────────────────────────────────────────────────────

/** True on touch screens (phones, tablets), live as that changes. */
export function useTouch(): boolean {
  const q = '(pointer: coarse)';
  const [touch, setTouch] = useState(() => typeof window !== 'undefined' && !!window.matchMedia?.(q).matches);
  useEffect(() => {
    const m = window.matchMedia?.(q);
    if (!m) return;
    const on = () => setTouch(m.matches);
    m.addEventListener('change', on);
    return () => m.removeEventListener('change', on);
  }, []);
  return touch;
}

type Props = {
  onStick: (x: number, y: number) => void;
  onAction: () => void;
  /** What the action button shows (🎣, ⚔️, ⛏️, 🧱…). */
  actionIcon: string;
  actionLabel: string;
};

export default function TouchControls({ onStick, onAction, actionIcon, actionLabel }: Props) {
  const base = useRef<HTMLDivElement>(null);
  const [knob, setKnob] = useState({ x: 0, y: 0 });
  const active = useRef<number | null>(null);

  const move = (e: React.PointerEvent) => {
    const b = base.current;
    if (!b || active.current !== e.pointerId) return;
    const r = b.getBoundingClientRect(), R = r.width / 2;
    let dx = e.clientX - (r.left + R), dy = e.clientY - (r.top + R);
    const d = Math.hypot(dx, dy), max = R * 0.6;
    if (d > max) { dx = (dx / d) * max; dy = (dy / d) * max; }
    setKnob({ x: dx, y: dy });
    onStick(dx / max, dy / max);
  };
  const end = (e: React.PointerEvent) => {
    if (active.current !== e.pointerId) return;
    active.current = null;
    setKnob({ x: 0, y: 0 });
    onStick(0, 0);
  };
  useEffect(() => () => onStick(0, 0), [onStick]);

  return (
    <>
      <div ref={base} className="touch-stick" aria-label="Swim joystick" role="application"
        onPointerDown={e => { active.current = e.pointerId; e.currentTarget.setPointerCapture(e.pointerId); move(e); }}
        onPointerMove={move} onPointerUp={end} onPointerCancel={end}>
        <span className="touch-knob" style={{ transform: `translate(${knob.x}px, ${knob.y}px)` }} />
      </div>
      <button type="button" className="touch-action" aria-label={actionLabel}
        onPointerDown={e => { e.preventDefault(); onAction(); }}>
        <span aria-hidden>{actionIcon}</span>
      </button>
    </>
  );
}
